import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigationGuard } from "../../../../context/NavigationGuardContext";
import { SalonReport } from "../../../../types/reports";
import { notifyError, notifySuccess } from "../../../../utils/notify";
import { httpStatusOf } from "../model/runErrors";

export type ViewPatch<V> = Partial<V> | ((current: V) => Partial<V>);

/** How one kind of saved view is read, compared and kept valid. */
export interface ViewRules<V> {
  /** A complete, validator-safe view from what's stored (maybe empty). */
  normalize: (stored: unknown) => V;
  equal: (a: V, b: V) => boolean;
  /** Applied after every edit (e.g. drop a sort on a hidden column). */
  sanitize?: (view: V) => V;
}

export interface UseViewDraftOptions<V> {
  report: SalonReport;
  rules: ViewRules<V>;
  /** Report display name (unsaved-changes dialog title). */
  name: string;
  /** PUT the view as the report's saved view; resolves with the updated
   *  report (the caller puts it back into the report list). */
  persistView: (view: V) => Promise<SalonReport>;
  /** Sidebar unsaved dot. Called with false on unmount. */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface ViewDraft<V> {
  saved: V;
  draft: V;
  dirty: boolean;
  saving: boolean;
  update: (patch: ViewPatch<V>) => void;
  save: () => Promise<boolean>;
  discard: () => void;
}

const identity = <V>(view: V): V => view;

/**
 * Draft vs saved view of a v2 report, whatever its layout (table or ledger):
 * every view change edits the draft; the report is dirty while the draft
 * differs from the saved view. Save view writes the draft, Discard drops it.
 * While dirty, the app-wide navigation guard asks Save / Discard changes
 * before leaving, and the sidebar shows the unsaved dot.
 */
export function useViewDraft<V>({
  report,
  rules,
  name,
  persistView,
  onDirtyChange,
}: UseViewDraftOptions<V>): ViewDraft<V> {
  const { normalize, equal } = rules;
  const sanitize = rules.sanitize ?? identity;
  const saved = useMemo(
    () => normalize(report.view_config),
    [normalize, report.view_config],
  );
  const [draft, setDraft] = useState<V>(saved);
  const [saving, setSaving] = useState(false);
  const dirty = !equal(draft, saved);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const dirtyCallback = useRef(onDirtyChange);
  dirtyCallback.current = onDirtyChange;

  // Clear the sidebar dot right away (not one render later) whenever the
  // draft is dropped or saved.
  const markClean = useCallback(() => dirtyCallback.current?.(false), []);

  const update = useCallback(
    (patch: ViewPatch<V>) => {
      setDraft((current) =>
        sanitize({
          ...current,
          ...(typeof patch === "function" ? patch(current) : patch),
        }),
      );
    },
    [sanitize],
  );

  const discard = useCallback(() => {
    setDraft(saved);
    markClean();
  }, [saved, markClean]);

  const save = useCallback(async (): Promise<boolean> => {
    setSaving(true);
    const sent = draftRef.current;
    try {
      const updated = await persistView(sent);
      const stored = normalize(updated.view_config);
      // Edits made while the PUT was in flight stay in the draft (and keep
      // the report dirty); otherwise adopt the stored view.
      const editedMeanwhile = !equal(draftRef.current, sent);
      setDraft((current) => (equal(current, sent) ? stored : current));
      if (!editedMeanwhile) markClean();
      notifySuccess("This is now the report's default view.", {
        title: "View saved",
      });
      return true;
    } catch (e) {
      if (httpStatusOf(e) === 400) {
        // The API rejected the view itself; retrying won't help.
        console.error(
          "Report view rejected:",
          e instanceof Error ? e.message : e,
        );
        notifyError("This view couldn't be saved.", {
          title: "Couldn't save view",
        });
      } else {
        console.error("Failed to save report view:", e);
        notifyError("Try again in a moment.", { title: "Couldn't save view" });
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [persistView, normalize, equal, markClean]);

  useNavigationGuard(`report:${report.id}`, {
    isDirty: dirty,
    name,
    onSave: save,
    onDiscard: discard,
  });

  useEffect(() => {
    dirtyCallback.current?.(dirty);
  }, [dirty]);
  useEffect(() => () => dirtyCallback.current?.(false), []);

  return { saved, draft, dirty, saving, update, save, discard };
}
